import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Modal } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { GuarantorDto, PatientGuarantorDto, TariffDto } from 'ts-types/api.types';
import Select from 'components/final-form/Select';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { required } from 'utils/validatorUtils';
import styled from 'styled-components';
import { getAllTariffs } from 'services/tariffServices';
import { getGuarantorData, searchGuarantorsForInput } from 'services/guarantorServices';
import axios, { CancelTokenSource } from 'axios';
import LoaderComponent from 'components/LoaderComponent';
import { noop } from 'utils/functionUtils';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';

const StyledLoaderComponent = styled(LoaderComponent)`
  font-weight: normal;
  font-size: 1rem;
`;

const StyledModal = styled(Modal)`
  && {
    width: 600px;
  }
`;

interface Props extends MessageSourceProps {
  modalOpen: boolean,
  onModalClose: () => void
  patientGuarantorDto?: PatientGuarantorDto,
  onUpsertPatientGuarantor: (patientGuarantor: PatientGuarantorDto) => void
}

interface State {
  tariffs: Array<DropdownOption>,
  tariffDtos: Array<TariffDto>,
  guarantors: Array<DropdownOption>,
  loadingData: boolean,
  selectedTariffId: number,
  cancelTokenSource: CancelTokenSource
}


class UpsertPatientGuarantorModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      tariffs: [],
      tariffDtos: [],
      guarantors: [],
      loadingData: false,
      selectedTariffId: -1,
      cancelTokenSource: axios.CancelToken.source()
    };
  }

  guarantorMapper = (response: Array<GuarantorDto>): Array<DropdownOption> => {
    return mapToDropdownOptionArrayWithTextGetter(response,
        (guarantor: GuarantorDto) => {
          const tariffType = guarantor.tariffType ? " - " + guarantor.tariffType : "";
          if (!guarantor.description) {
            return guarantor.bagNumber + tariffType;
          }
          if (!guarantor.bagNumber) {
            return guarantor.description + tariffType;
          }
          return `${guarantor.description} - ${guarantor.bagNumber}` + tariffType;
        });
  };

  searchGuarantors = (search: string): Promise<Array<GuarantorDto>> => {

    const {cancelTokenSource, selectedTariffId, tariffDtos} = this.state;
    const selectedTariffs = tariffDtos.filter(tariff => {
      if (selectedTariffId === tariff.id) {
        return tariff;
      }
    });

    const selectedTypeCode = selectedTariffs && selectedTariffs.length > 0 ? selectedTariffs[0].typeCode : "";

    return searchGuarantorsForInput(search, cancelTokenSource, 75, selectedTypeCode);
  };

  componentDidUpdate({modalOpen: prevModalOpen}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {modalOpen} = this.props;

    if (modalOpen != prevModalOpen && modalOpen) {
      this.loadData();
    }
  }

  loadData = () => {
    const {patientGuarantorDto} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      guarantors: [],
      loadingData: true
    });

    getAllTariffs(cancelTokenSource)
        .then(response => {
          this.setState({
            tariffDtos: response,
            tariffs: mapToDropdownOptionArray(response, "description")
          })
        })
        .catch(noop)
        .finally(() => {
          if (!patientGuarantorDto) {
            this.setState({
              loadingData: false
            })
          }
        });

    if (patientGuarantorDto) {
      getGuarantorData(patientGuarantorDto.guarantorId, cancelTokenSource)
          .then(response => {

            let guarantorText;

            if (!response.description) {
              guarantorText = response.bagNumber;
            }
            if (!response.bagNumber) {
              guarantorText = response.description;
            }
            guarantorText = `${response.description} - ${response.bagNumber}`;

            const guarantor = {
              key: patientGuarantorDto.guarantorId,
              text: guarantorText,
              value: patientGuarantorDto.guarantorId
            };

            this.setState({
              guarantors: [guarantor]
            })
          })
          .catch(noop)
          .finally(() => {
            this.setState({
              loadingData: false
            })
          })
    }

  };

  initialPatientGuarantorValues = (): Partial<PatientGuarantorDto> => {

    const {tariffs, guarantors} = this.state;

    return {
      tariffId: tariffs && tariffs.length > 0 ? tariffs[0].value : undefined,
      guarantorId: guarantors && guarantors.length > 0 ? guarantors[0].value : undefined,
      disableXmlInvoice: false
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    this.setState({
      selectedTariffId: values.tariffId
    })
  };


  handleSubmit = (values: Partial<PatientGuarantorDto>) => {

    const {patientGuarantorDto: presentPatientGuarantor, onUpsertPatientGuarantor} = this.props;
    const {tariffs, cancelTokenSource} = this.state;

    const tariff = tariffs.find(tariff => tariff.value == values.tariffId);

    let patientGuarantorDto: PatientGuarantorDto = {
      id: presentPatientGuarantor ? presentPatientGuarantor.id : undefined,
      tariffId: values.tariffId ? values.tariffId : -1,
      guarantorId: values.guarantorId ? values.guarantorId : -1,
      guarantor: "",
      tariff: tariff ? tariff.text.toString() : "",
      mtgNumber: values.mtgNumber,
      companyNumber: values.companyNumber,
      discount: values.discount,
      fromDate: values.fromDate,
      toDate: values.toDate,
      healthCardNumber: values.healthCardNumber,
      disableXmlInvoice: values.disableXmlInvoice ? values.disableXmlInvoice : false
    };

    if (values.guarantorId) {
      getGuarantorData(values.guarantorId, cancelTokenSource)
          .then(response => {
            const guarantorStreet = response.street ? `, ${response.street}` : "";
            const guarantorCity = response.city ? `, ${response.city}` : "";
            patientGuarantorDto.guarantor = response.description + guarantorCity + guarantorStreet;
          })
          .catch(noop)
          .finally(() => {
            onUpsertPatientGuarantor(patientGuarantorDto);
          })
    }

  };

  render(): React.ReactNode {

    const {modalOpen, onModalClose, getMessage, patientGuarantorDto = this.initialPatientGuarantorValues()} = this.props;
    const {loadingData} = this.state;

    return (
        <StyledModal
            open={modalOpen}
            onClose={onModalClose}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            <span>
              {getMessage("insurance")}
            </span>
            {
              loadingData &&
              <StyledLoaderComponent message={getMessage("loadingData")} inverted/>
            }
          </Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={patientGuarantorDto}
                subscription={{pristine: true, values: true}}
                render={this.renderInsuranceForm}
            />
          </FormModalContentContainer>
        </StyledModal>
    );
  }

  renderInsuranceForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onModalClose} = this.props;
    const {tariffs, guarantors} = this.state;

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("tariff")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="tariffId"
                        component={Select}
                        validate={required}
                        options={tariffs}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("guarantor")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="guarantorId"
                        component={Select}
                        validate={required}
                        options={guarantors}
                        remoteSearch
                        remoteSearchMethod={this.searchGuarantors}
                        searchResultToOptionMapper={this.guarantorMapper}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("validAfter")}:</Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                    <Field
                        name="fromDate"
                        component={DatePicker}
                        disable
                    />
                  </Grid.Column>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("validBefore")}:</Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                    <Field
                        name="toDate"
                        component={DatePicker}
                        disabledBefore={disabledBefore}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("healthCardNumber")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="healthCardNumber"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("mtgNumber")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="mtgNumber"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("companyNumber")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="companyNumber"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} verticalAlign="middle">{getMessage("surcharge")}:</Grid.Column>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Field
                        name="discount"
                        component={Input}
                        onKeyDown={ensureOnlyNumericInput}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3}>
                  </Grid.Column>
                  <Grid.Column width={13}>
                    <Field
                        name="disableXmlInvoice"
                        component={CheckBox}
                        label={getMessage("disableXmlInvoice")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
              <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" onClick={onModalClose}
                      disabled={submitting}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  }
}

export default withMessages(UpsertPatientGuarantorModal);
